import { Drop } from "@/components/Drop/Drop";
import { ImageBox } from "@/components/ImageBox/ImageBox";
import { $selectedImage } from "@/stores/selectedImage";
import { $uploads, UploadsService } from "@/stores/uploads";
import { Modal, Text } from "@mantine/core";
import { useStore } from "@nanostores/react";
import { createContext, ReactNode, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export type UploadModalContextType = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

export const UploadModalContext = createContext<
  UploadModalContextType | undefined
>(undefined);

export function UploadModalProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(searchParams.has("upload"));
  const uploads = useStore($uploads);

  useEffect(() => {
    if (searchParams.has("upload")) setIsOpen(true);
  }, [searchParams]);

  const open = () => setIsOpen(true);
  const close = () => {
    setIsOpen(false);
    if (searchParams.has("upload")) {
      searchParams.delete("upload");
      setSearchParams(searchParams);
    }
  };

  return (
    <UploadModalContext.Provider value={{ isOpen, open, close }}>
      <Modal
        onClose={close}
        opened={isOpen}
        withCloseButton
        size="xl"
        zIndex={9999}
        title={
          <div className="flex flex-col gap-0">
            <Text c="dark" fw={700}>
              Uploaded Images
            </Text>
            <Text c="gray.7" size="xs">
              Pick any image that you would like to print and add
              configurations.
            </Text>
          </div>
        }
      >
        <div className="flex flex-col items-center justify-center">
          <div className="h-[15px]" />
          <div className="h-[200px] w-[95vw] max-w-[40vw]">
            <Drop />
          </div>
          <div className="h-[15px]" />
          <div className="grid max-h-[700px] w-full grid-cols-5 gap-0 overflow-y-auto">
            {uploads.map(image => (
              <ImageBox
                key={image.url}
                image={image}
                onSelect={image => {
                  $selectedImage.set(image);
                  window.location.href = "/visualizer";
                  close();
                }}
                onDelete={async image => {
                  await UploadsService.deleteImageOptimistic(image);
                }}
              />
            ))}
          </div>
          <div className="h-[50px]" />
        </div>
      </Modal>
      {children}
    </UploadModalContext.Provider>
  );
}
